import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Home } from './routes/Home.js';
import { Create } from './routes/Create.js';
import { Join } from './routes/Join.js';
import { Room } from './routes/Room.js';
import { Admin } from './routes/Admin.js';
import { Leaderboard } from './routes/Leaderboard.js';
import { Builder } from './routes/Builder.js';
import { EmbedEntry } from './routes/EmbedEntry.js';
import { UpdatePrompt } from './components/UpdatePrompt.js';
import { BurgerMenu } from './components/BurgerMenu.js';
import { PWAInstallBanner } from './components/PWAInstallBanner.js';
import { useThemeStore } from './store/theme.js';
import { usePlayerStore } from './store/player.js';
import { registerUserGame } from './platform/game-registry.js';
import { isEmbedMode } from './platform/embed.js';
import { apiFetch } from './platform/api.js';

/**
 * Keeps the shell's cached credit balance honest.
 *
 * Requires a JWT (room or identity); without a token the balance stays at 0 —
 * the endpoint refuses to disclose anyone's balance to an unauthenticated
 * caller.
 *
 * Re-reads on every route change and whenever the tab regains focus, not just
 * once on mount. The wallet games (coin-duel, collapse-factor, kamba-adeema,
 * kana-mutti, sky-crash, aether-dynasty and the racing pair) move points from
 * inside their own embedded SPA and never push the
 * new balance back up, so leaving one left the header showing the pre-game
 * figure until a full reload. Must live inside the router to see `useLocation`.
 */
function CreditsSync() {
  const { playerId, token, setCredits } = usePlayerStore();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!playerId || !token) return;
    let cancelled = false;
    const load = () => {
      apiFetch('/api/credits/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { balance: number } | null) => {
          if (!cancelled && data) setCredits(data.balance);
        })
        .catch(() => {});
    };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
    };
  }, [playerId, token, setCredits, pathname]);

  return null;
}

export function App() {
  const { mode } = useThemeStore();

  // Seed native user identity from URL params or postMessage bridge.
  // This runs once on mount so the mobile app can pass ?playerId=&displayName=
  // when opening the WebView, overriding the auto-generated UUID.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nativePlayerId = params.get('playerId');
    const nativeDisplayName = params.get('displayName');

    if (nativePlayerId) usePlayerStore.getState().setPlayerId(nativePlayerId);
    if (nativeDisplayName) usePlayerStore.getState().setDisplayName(nativeDisplayName);

    if (nativePlayerId ?? nativeDisplayName) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('playerId');
      clean.searchParams.delete('displayName');
      window.history.replaceState({}, '', clean.toString());
    }

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(String(event.data)) as { type?: string; playerId?: string; displayName?: string };
        if (data.type === 'AUTH_CONTEXT') {
          if (data.playerId) usePlayerStore.getState().setPlayerId(data.playerId);
          if (data.displayName) usePlayerStore.getState().setDisplayName(data.displayName);
        }
      } catch {
        // ignore non-JSON messages
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Load published user-created games into the frontend registry
  useEffect(() => {
    apiFetch('/api/ai-builder/published')
      .then((r) => r.json())
      .then((data: { games: Array<{ id: string; meta: { name?: string; description?: string; minPlayers?: number; maxPlayers?: number }; hasCustomUI?: boolean }> }) => {
        for (const g of data.games ?? []) {
          registerUserGame({
            id: g.id,
            name: g.meta.name ?? g.id,
            description: g.meta.description ?? '',
            minPlayers: g.meta.minPlayers ?? 2,
            maxPlayers: g.meta.maxPlayers ?? 8,
            estimatedDurationMinutes: 10,
            tags: [],
            hasCustomUI: g.hasCustomUI ?? false,
          });
        }
      })
      .catch(() => {});
  }, []);

  // Apply / remove the `dark` class on <html> based on current mode
  useEffect(() => {
    const html = document.documentElement;

    function apply(dark: boolean) {
      html.classList.toggle('dark', dark);
    }

    if (mode === 'dark') { apply(true); return; }
    if (mode === 'light') { apply(false); return; }

    // auto — follow system preference
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    apply(mq.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const embed = isEmbedMode();

  return (
    <BrowserRouter>
      <CreditsSync />
      <div className={`min-h-dvh bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 antialiased${embed ? ' overscroll-none touch-manipulation' : ''}`}>
        {!embed && <BurgerMenu />}
        {!embed && <UpdatePrompt />}
        {!embed && <PWAInstallBanner />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/create" element={<Create />} />
          <Route path="/join" element={<Join />} />
          <Route path="/room/:roomId" element={<Room />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/build" element={<Builder />} />
          {/* Entry point when opened from the SC Mobile App WebView */}
          <Route path="/embed" element={<EmbedEntry />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
